/** Tier score display for Analysis tab (new scoring engine UX). */

import { calcRSI } from "./rsiScoring.js";

function n(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

function emaLatest(closes, period) {
  if (!closes?.length || closes.length < period) return null;
  const chrono = [...closes].reverse();
  let ema = chrono.slice(0, period).reduce((s, v) => s + v, 0) / period;
  const k = 2 / (period + 1);
  for (let i = period; i < chrono.length; i++) {
    ema = chrono[i] * k + ema * (1 - k);
  }
  return ema;
}

function donchian20(highs, closes) {
  if (!highs?.length || highs.length < 21) return { upper: null, lower: null, mid: null, breaking: false };
  const upper = Math.max(...highs.slice(1, 21));
  const lower = Math.min(...highs.slice(1, 21));
  const mid = (upper + lower) / 2;
  const breaking = closes?.[0] != null && closes[0] > upper;
  const aboveMid = closes?.[0] != null && closes[0] > mid;
  return { upper, lower, mid, breaking, aboveMid };
}

function avgVol(volumes, len) {
  const slice = volumes?.slice(0, len).filter((v) => v > 0);
  if (!slice?.length) return null;
  return slice.reduce((s, v) => s + v, 0) / slice.length;
}

function volContracting(volumes) {
  const v5 = avgVol(volumes, 5);
  const v20 = avgVol(volumes, 20);
  return v5 != null && v20 != null && v5 < v20;
}

function rsiCrossAbove50DaysAgo(closes) {
  if (!closes?.length || closes.length < 20) return null;
  for (let d = 0; d < Math.min(12, closes.length - 16); d++) {
    const r0 = calcRSI(closes.slice(d));
    const r1 = calcRSI(closes.slice(d + 1));
    if (r0 != null && r1 != null && r1 < 50 && r0 >= 50) return d;
  }
  return null;
}

export function fmtTierScore(v) {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  if (v === 0) return "+0";
  return v > 0 ? `+${v}` : `${v}`;
}

export function buildTierScoreRows(result) {
  const detailed = buildDetailedScoreBreakdown(result);
  if (!detailed) return null;
  const rows = detailed.tiers.map((t) => ({ key: t.key, label: t.title.replace(/^TIER \d — /, ""), score: t.subtotal }));
  rows.push({ key: "confluence", label: "Confluence bonus", score: detailed.confluenceBonus });
  return { rows, rawTotal: detailed.rawTierTotal, capped: detailed.finalScore };
}

/** Full tier groups with condition rows for Analysis tab section 2. */
export function buildDetailedScoreBreakdown(result) {
  if (!result) return null;
  const closes = result.closes;
  const highs = result.highs;
  const volumes = result.volumes;
  const price = result.price;
  const ema50 = closes?.length ? emaLatest(closes, 50) : result.sm50;
  const ema200 = closes?.length ? emaLatest(closes, 200) : result.sm200;
  const pAbove50 = price != null && ema50 != null && price > ema50;
  const e50Above200 = ema50 != null && ema200 != null && ema50 > ema200;
  const pAbove200 = price != null && ema200 != null && price > ema200;

  const structureRows = [
    { label: "Price > EMA50", value: ema50 != null ? `$${price?.toFixed(2)} vs $${ema50.toFixed(2)}` : "—", pass: pAbove50, points: pAbove50 ? 3 : 0 },
    { label: "EMA50 > EMA200", value: ema50 != null && ema200 != null ? `${e50Above200 ? "Golden" : "Bear"} structure` : "—", pass: e50Above200, points: e50Above200 ? 3 : 0 },
    { label: "Price > EMA200", value: ema200 != null ? `$${ema200.toFixed(2)}` : "—", pass: pAbove200, points: pAbove200 ? 2 : 0 },
  ];

  const rvol = result.rvol?.ratio;
  const volExp = rvol != null && rvol >= 1.5 || result.volBreakout?.volConfirmed;
  const cmf = result.cmf;
  const cmfPass = cmf != null && cmf > 0.05;
  const cmfPts = cmf != null && cmf > 0.1 ? 2 : cmfPass ? 1 : 0;
  const obvUp = result.obv?.rising || result.obv?.confirming;
  const volumeRows = [
    { label: "Volume expansion", value: rvol != null ? `${rvol.toFixed(1)}× avg` : "—", pass: !!volExp, points: volExp ? 3 : 0 },
    { label: "CMF", value: cmf != null ? cmf.toFixed(2) : "—", pass: cmfPass, points: cmfPts },
    { label: "OBV rising", value: obvUp ? "Rising" : "Flat/falling", pass: !!obvUp, points: obvUp ? 2 : 0 },
  ];

  const fib500 = result.fibs?.fib500 ?? result.fibSwing?.fib500;
  const fibLabel = result.fibLevel ? `Fib ${result.fibLevel}` : null;
  const atFib = result.fibLevel != null;
  const fibRows = [
    {
      label: atFib ? `At ${fibLabel}` : "At key fib level",
      value: fib500 != null ? `$${fib500.toFixed(2)}` : "—",
      pass: atFib,
      points: atFib ? 3 : 0,
    },
    { label: "Active bounce", value: result.fibBounce ? "Bounce confirmed" : "—", pass: !!result.fibBounce, points: result.fibBounce ? 1 : 0 },
  ];

  const don = donchian20(highs, closes);
  const donchianRows = [
    { label: "Above midline", value: don.mid != null ? `$${don.mid.toFixed(2)}` : "—", pass: !!don.aboveMid, points: don.aboveMid ? 2 : 0 },
    { label: "Donchian breakout", value: don.breaking ? "Confirmed" : "Not yet", pass: !!don.breaking, points: don.breaking ? 3 : 0 },
  ];

  const rsi = result.rsi;
  const rsiDiv = result.rsiDiv;
  const rsiAbove50 = rsi != null && rsi > 50;
  const rsiMomentumPts = rsi != null && rsi > 70 ? 1 : rsiAbove50 ? 1 : 0;
  const momentumRows = [
    { label: rsi != null && rsi > 70 ? "RSI strong momentum" : "RSI above 50", value: rsi != null ? `${rsi.toFixed(0)}` : "—", pass: rsiAbove50, points: rsiMomentumPts },
    { label: "RSI bullish divergence", value: rsiDiv?.bullish ? "Detected" : "None", pass: !!rsiDiv?.bullish, points: rsiDiv?.bullish ? 2 : 0 },
  ];

  const tiers = [
    { key: "structure", title: "TIER 1 — STRUCTURE", rows: structureRows },
    { key: "volume", title: "TIER 2 — VOLUME & FLOW", rows: volumeRows },
    { key: "fibonacci", title: "TIER 3 — FIBONACCI", rows: fibRows },
    { key: "donchian", title: "TIER 4 — DONCHIAN", rows: donchianRows },
    { key: "momentum", title: "TIER 5 — MOMENTUM", rows: momentumRows },
  ].map((t) => ({ ...t, subtotal: t.rows.reduce((s, r) => s + r.points, 0) }));

  const c = result.confluence;
  const confluenceBonus = c?.active && n(c.bonus) > 0 ? n(c.bonus) : 0;
  const rawTierTotal = tiers.reduce((s, t) => s + t.subtotal, 0) + confluenceBonus;

  const sb = result.scoreBreakdown?.multipliers;
  const beforeTide = sb?.buyScoreBeforeTide ?? sb?.buyScoreAfterWeekly ?? result.buyScore;
  const afterTide = sb?.buyScoreAfterTide ?? beforeTide;
  const tideMult = sb?.tideMultiplier ?? 1;
  const tideDelta = Math.round((afterTide - beforeTide) * 2) / 2;
  const finalScore = result.buyScore;
  const macroAdj = Math.round((finalScore - afterTide) * 2) / 2;

  return {
    tiers,
    confluenceBonus,
    rawTierTotal,
    tideMult,
    tideDelta,
    macroAdj,
    finalScore,
    ema50,
    ema200,
    don,
    volContracting: volContracting(volumes),
    rsiCrossDays: rsiCrossAbove50DaysAgo(closes),
  };
}

export function buildFibPriceStoryExtra(result) {
  const fib500 = result.fibs?.fib500 ?? result.fibSwing?.fib500;
  if (result.fibLevel && fib500 != null) {
    const pct = result.fibLevel;
    return `Price is pulling back to the ${pct} retracement ($${fib500.toFixed(2)}) — a textbook entry zone if volume stays quiet.`;
  }
  if (fib500 != null) {
    return `Price is between fib levels — watch $${fib500.toFixed(2)} (50%) as next support.`;
  }
  return null;
}

export const CONFLUENCE_HEADLINE = {
  FIB_CONFLUENCE_LONG: "🎯 FIB PULLBACK CONFLUENCE",
  DONCHIAN_BREAKOUT_CONFIRMED: "⚡ DONCHIAN BREAKOUT CONFLUENCE",
  FIB_BOUNCE_DIVERGENCE: "↗ FIB BOUNCE CONFLUENCE",
  DISTRIBUTION_WARNING: "⚠ DISTRIBUTION CONFLUENCE",
};
